import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import type { Account } from '@interfaces/account';
import type { ClientGroup } from '@interfaces/clientGroup';
import type { Integration } from '@interfaces/integrations';
import type { BareLocalization } from '@interfaces/localization';
import type {
  AuthenticationConnections,
  EnabledJurisdictions,
  OrganizationThemeTokens,
} from '@interfaces/organization';
import type {
  OrganizationUserEntityRelationTypes,
  UserNotificationPreference,
} from '@interfaces/organizationUser';
import { OrganizationUserAccessTypes } from '@interfaces/organizationUser';
import type { Role } from '@interfaces/role';
import { NavigationStyles } from '@interfaces/role';
import type { TranslatedString } from '@interfaces/shared';
import { EntityTypes } from '@interfaces/user';
import type {
  CurrencyCodes,
  LanguageCodes,
  Languages,
} from '@onevesthq/ov-enums';
import { languageCodeToLanguageMap } from '@onevesthq/ov-translations';
import { DocumentHead } from '@ovComponents/1-primative/documentHead/documentHead';
import { adjust } from '@ovComponents/legacyColor';
import { useLocalStorageValue, useSessionStorageValue } from '@react-hookz/web';
import { delay } from '@utils/common';
import { useActiveOrganizationId } from '@utils/useActiveOrganizationId';
import { useSetTheme } from 'hephaestus/web';
import omitDeep from 'omit-deep-lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import i18n, {
  getBackendLanguage,
  getOrgDefaultLanguage,
  LOCALIZATION_DEFAULTS,
  setBackendLanguage,
  TRANSLATED_STRING_FIELDS,
} from '../assets/i18n/config';
import type {
  DowntimeSchedule,
  FeatureFlagTypes,
  Pages,
} from '../generated/graphql';
import type { CustodianConnection } from '../interfaces/custodianConnection';
import type { CustomAttributeLabel } from '../interfaces/customAttributeLabels';
import type { RepCode } from '../interfaces/repCode';
import {
  CONDITION,
  FETCH_PAGE_CONFIGURATION,
} from '../ovComponents/5-page/pageConfiguration/pageConfiguration.queries';
import filterHouseholdsWithAccess from '../utils/filterHouseholdsWithAccess';
import { FlagsmithFlags, useFlagsmithFlags } from '../utils/flagsmith';
import { useDowntimeSchedules } from '../utils/useDowntimeSchedules';
import { useAuthContext } from './ovApolloProvider';

type Widget = {
  id: string;
  type: string;
  options: any;
};

type Tab = {
  id: string;
  icon: string;
  label: TranslatedString;
  widgets: Widget[];
};

export type PageConfiguration = {
  id: string;
  tabs: Tab[];
  options?: any;
};

export type UserContextProps = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  language?: Languages;
  accessType?: OrganizationUserAccessTypes;
  notificationPreferences?: UserNotificationPreference[];
  role?: Role;
  avatar?: string;
  repCodes?: RepCode[];
  viewFullClientGroup?: boolean;
  organization?: {
    id: string;
    translatedName?: TranslatedString;
    subdomain: string;
    theme?: { sideBarColor?: string; logo?: string };
    translatedSupportUrl?: TranslatedString;
    translatedHelpCentreUrl?: TranslatedString;
    enableContactSupport?: boolean;
    enableResourcesAndArticles?: boolean;
    logoutRedirectUrl?: string;
    enableInactivityTimeOut?: boolean;
    inactivityTimeoutInMinutes?: number;
    clientHostedLoginURL?: string;
    clientHostedLoginRedirectUri?: string;
    defaultSignUpRole?: { id: string };
  };
  entities?: [
    {
      entity: {
        id: string;
        affiliateOnly?: boolean;
        type?: EntityTypes;
        entityName?: string;
        firstName?: string;
        lastName?: string;
        primaryEmail?: string;
        households?: ClientGroup[];
      };
      role?: Role;
      accounts: Account[];
      relation: OrganizationUserEntityRelationTypes;
    },
  ];
  entitiesCount?: number;
  repCodeEntities?: [
    {
      entity: {
        id: string;
        affiliateOnly?: boolean;
        type: EntityTypes;
        entityName: string;
        firstName: string;
        lastName: string;
        primaryEmail: string;
        households: ClientGroup[];
      };
      repCodes: RepCode[];
      accounts: Account[];
    },
  ];
};

export type SetUserContextType = React.Dispatch<
  React.SetStateAction<UserContextProps>
>;

export type UserContextType = {
  userContext: UserContextProps;
  setUserContext: SetUserContextType;
  activeOrganization: Omit<ActiveOrganizationProps, 'availableFeatureFlags'>;
  setActiveOrganizationId: (organizationId: string | undefined) => void;
  activeEntity: ActiveEntityProps | undefined;
  setActiveEntity: React.Dispatch<React.SetStateAction<any | undefined>>;
  activeHousehold: ClientGroup | undefined;
  setActiveHousehold: React.Dispatch<
    React.SetStateAction<ClientGroup | undefined>
  >;
  activeLanguage: Languages | undefined;
  setActiveLanguage: React.Dispatch<React.SetStateAction<Languages>>;
  organizationIds: string[];
  setOrganizationIds: React.Dispatch<React.SetStateAction<string[]>>;
  userId: string | undefined;
  setUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
  activeCurrency: string;
  setActiveCurrency: React.Dispatch<React.SetStateAction<string>>;
  availableCurrencies: string[];
  setAvailableCurrencies: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  organizationLoading: boolean;
  organizationFetchFailed: boolean;
  integrations: Integration[];
  custodianConnection: CustodianConnection;
  baseCurrency: string;
  closed: boolean;
  displayMenuBar: boolean;
  setClosed: (closed: boolean) => void;
  avatar?: string;
  refetch: () => void;
  upcomingDowntimeSchedules: DowntimeSchedule[];
  applicableDowntimeSchedule: DowntimeSchedule | undefined;
  featureFlags: Set<FlagsmithFlags>;
  organizationFeatureFlags: Set<FeatureFlagTypes>;
  accessiblePages: Set<string>;
  permissions: Set<string>;
};

export type ActiveOrganizationProps = {
  id?: string;
  translatedName?: TranslatedString;
  parents?: { id: string }[];
  publicParents?: { id: string }[];
  availableFeatureFlags?: FeatureFlagTypes[];
  themeTokens?: OrganizationThemeTokens;
  theme?: { logo?: string; sideBarColor?: string; graphColors?: string[] };
  reviewTransactions?: boolean;
  displayMenuBar?: boolean;
  allowLogout?: boolean;
  displayCurrency?: boolean;
  allowPortfolioPerGoal?: boolean;
  useParentIntegrations?: boolean;
  allowPostOptimizationEditing?: boolean;
  applicableLocalization: BareLocalization;
  jurisdictions: EnabledJurisdictions;
  minRecurringDepositCents?: number;
  profileReviewTimeInMonths?: number;
  requireSubTradeRequestApproval?: boolean;
  allowViewSubTradeRequestPairs?: boolean;
  allowPendingTransactions?: boolean;
  defaultTradingProcess?: string;
  bulkTradingProcess?: string;
  optimizerProcess?: string;
  faviconLink?: string;
  browserTabTitle?: string;
  translatedSupportUrl?: TranslatedString;
  translatedHelpCentreUrl?: TranslatedString;
  enableContactSupport?: boolean;
  enableResourcesAndArticles?: boolean;
  logoutRedirectUrl?: string;
  enableInactivityTimeOut?: boolean;
  inactivityTimeoutInMinutes?: number;
  clientHostedLoginURL?: string;
  clientHostedLoginRedirectUri?: string;
  minDepositCents?: number;
  minInitialDepositCents?: number;
  productShelfCurrencies?: CurrencyCodes[];
  errorPageMessage?: TranslatedString;
  errorPageDescription?: TranslatedString;
  errorPageButtonText?: TranslatedString;
  errorPageBackgroundColor?: string;
  errorPageBackgroundImage?: string;
  errorPageShowNavbar?: boolean;
  enableFullCashTransfers?: boolean;
  enablePartialCashTransfers?: boolean;
  enableAllInKindTransfers?: boolean;
  defaultAuthenticationConnection?: AuthenticationConnections;
  enableLoginSettings?: boolean;
  loginSettingsTitle?: TranslatedString;
  loginUrl?: string;
  cashOnHoldToTradeDays?: number;
  cashOnHoldToWithdrawDays?: number;
  cancelTransferCutoffDays?: number;
  hideAutoUpdatePrices?: boolean;
  feeSetting?: { enabled?: boolean };
  defaultCustodianConnection?: CustodianConnection;
  customAttributeLabels?: CustomAttributeLabel[];
};

export type ActiveEntityProps = {
  id?: string;
  type?: string;
  entityName?: string;
  firstName?: string;
  lastName?: string;
  primaryEmail?: string;
  households?: ClientGroup[];
  availableFeatureFlags?: string[];
};

const PAGE_CONFIGURATION = (isConditionEnabled = false) => `#graphql
  id
  type
  options
  tabs {
    id
    icon
    translatedUrl { ${TRANSLATED_STRING_FIELDS} }
    label { ${TRANSLATED_STRING_FIELDS} }
    workflow { id state }
    widgets { id type options ${isConditionEnabled ? CONDITION : ''} }
    ${isConditionEnabled ? CONDITION : ''}
  }
`;

const HOME_PAGE_CONFIGURATION = `#graphql
  id
  translatedName { ${TRANSLATED_STRING_FIELDS} }
  widgets { id type options }
`;

const STATIC_PAGE_WIDGET_CONFIGURATION = `#graphql
  id
  widget { id type options }
`;

const ENTITY = `#graphql
  id
  type
  affiliateOnly
  entityName
  firstName
  lastName
  primaryEmail
  households {
    id
    relationships { accessType user { id } }
    organization { id }
  }
`;

/** Slim ME query for initial load: no entities/repCodeEntities lists (avoids hang with many entities). */
export const ME_SLIM = (permissions?: string[]) => gql`
  query meSlim {
    me {
      id
      firstName
      lastName
      language
      email
      notificationPreferences {
        notificationGroup {
          id
          name { ${TRANSLATED_STRING_FIELDS} }
          description { ${TRANSLATED_STRING_FIELDS} }
          notificationChannels { type value allowEditByClient }
        }
        preferences { enableEmail enableInApp enablePush }
      }
      phone
      accessType
      viewFullClientGroup
      avatar
      role {
        id
        permissions
        accessiblePages
        ${permissions?.includes('read:dashboards') ? `dashboards { id translatedName { ${TRANSLATED_STRING_FIELDS} } }` : ''}
        navigationStyle
        makeHouseholdDefaultView
      }
      organization {
        id
        subdomain
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        themeTokens
        supportUrl
        helpCentreUrl
        logoutRedirectUrl
        enableSingleLogout
        enableInactivityTimeOut
        inactivityTimeoutInMinutes
        clientHostedLoginURL
        clientHostedLoginRedirectUri
        defaultSignUpRole { id }
      }
      ${
        permissions?.includes('read:organization_user_entities')
          ? `#graphql
        entitiesCount
      `
          : ''
      }
      ${
        permissions?.includes('read:rep_code')
          ? `#graphql
        repCodes { code }
      `
          : ''
      }
    }
  }
`;

/** Fetches only entities/repCodeEntities (phase 2 after ME_SLIM). */
export const ME_ENTITIES = (permissions?: string[]) => gql`
  query meEntities {
    me {
      accessType
      ${
        permissions?.includes('read:organization_user_entities')
          ? `#graphql
        entities {
          entity { ${ENTITY} }
          accounts { id }
          role { id }
          relation
        }
        entitiesCount
      `
          : ''
      }
      ${
        permissions?.includes('read:rep_code')
          ? `#graphql
        repCodeEntities {
          entity { ${ENTITY} }
          accounts { id }
        }
      `
          : ''
      }
    }
  }
`;

export const ME = (permissions?: string[]) => gql`
  query me {
    me {
      id
      firstName
      lastName
      language
      email
      notificationPreferences {
        notificationGroup {
          id
          name { ${TRANSLATED_STRING_FIELDS} }
          description { ${TRANSLATED_STRING_FIELDS} }
          notificationChannels { type value allowEditByClient }
        }
        preferences { enableEmail enableInApp enablePush }
      }
      phone
      accessType
      viewFullClientGroup
      avatar
      role {
        id
        permissions
        accessiblePages
        ${permissions?.includes('read:dashboards') ? `dashboards { id translatedName { ${TRANSLATED_STRING_FIELDS} } }` : ''}
        navigationStyle
        makeHouseholdDefaultView
      }
      organization {
        id
        subdomain
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        themeTokens
        translatedSupportUrl { ${TRANSLATED_STRING_FIELDS} }
        translatedHelpCentreUrl { ${TRANSLATED_STRING_FIELDS} }
        logoutRedirectUrl
        enableSingleLogout
        enableInactivityTimeOut
        inactivityTimeoutInMinutes
        clientHostedLoginURL
        clientHostedLoginRedirectUri
        defaultSignUpRole { id }
      }
      ${
        permissions?.includes('read:organization_user_entities')
          ? `#graphql
        entities {
          entity { ${ENTITY} }
          accounts { id }
          role { id }
          relation
        }
        entitiesCount
      `
          : ''
      }
      ${
        permissions?.includes('read:rep_code')
          ? `#graphql
        repCodes { code }
        repCodeEntities {
          entity { ${ENTITY} }
          accounts { id }
        }
      `
          : ''
      }
    }
  }
`;

export const ME_ROLE_PAGE_CONFIGURATIONS = gql`
  query meRolePageConfigurations {
    me {
      role {
        individualPageConfiguration { ${PAGE_CONFIGURATION()} }
        nonIndividualPageConfiguration { ${PAGE_CONFIGURATION()} }
        goalPageConfiguration { ${PAGE_CONFIGURATION()} }
        accountPageConfiguration { ${PAGE_CONFIGURATION()} }
        accountGroupPageConfiguration { ${PAGE_CONFIGURATION()} }
        householdPageConfiguration { ${PAGE_CONFIGURATION()} }
        subAccountPageConfiguration { ${PAGE_CONFIGURATION()} }
        homePageConfiguration { ${HOME_PAGE_CONFIGURATION} }
      }
    }
  }
`;

export const FETCH_ORGANIZATION = (
  canReadCustodianConnection?: boolean,
  useFeeEnableToggle?: boolean,
) => gql`
  query fetchOrganization($organizationId: String!) {
    fetchOrganization(organizationId: $organizationId) {
      organization {
        id
        parents {
          id
        }
        publicParents {
          id
        }
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        availableFeatureFlags
        translatedSupportUrl { ${TRANSLATED_STRING_FIELDS} }
        translatedHelpCentreUrl { ${TRANSLATED_STRING_FIELDS} }
        enableContactSupport
        enableResourcesAndArticles
        logoutRedirectUrl
        enableSingleLogout
        enableInactivityTimeOut
        inactivityTimeoutInMinutes
        ${
          canReadCustodianConnection
            ? `#graphql
          defaultCustodianConnection {
            id
            name
            default
            type
            metadata { key value }
            enableFetchCustodianStatistics
            enableFetchCustodianSnapshotHistory
            enableFetchCustodianTransactions
            enableFetchCustodianSuitability
            enableFetchCustodianAffiliates
            enableFetchCustodianUpcomingTransactions
            enableFetchCustodianStatements
            enableFetchCustodianCustomFields
            enableFetchCustodianProjectedIncome
            enableFetchCustodianCustomFields
            enableFetchCustodianNotifications
            enableCancelCustodianDepositTransfer
            enableChallengedCustodianDepositTransfer
            enableCustodianCredentialsManager
            enableSaveInternalRecordsOfCustodianDepositTransfers
            allowCashAsDepositTarget
            organization { id }
            accountTypeSettings { type baseCurrency availableCurrencies isMultiCurrencyEnabled }
            disableCorpInventoryAccount
          }
        `
            : ''
        }
        themeTokens
        useParentIntegrations
        useParentWebhooks
        allowPostOptimizationEditing
        requireSubTradeRequestApproval
        allowViewSubTradeRequestPairs
        allowPendingTransactions
        defaultTradingProcess
        bulkTradingProcess
        optimizerProcess
        minRecurringDepositCents
        profileReviewTimeInMonths
        theme { sideBarColor logo }
        applicableLocalization { countries languages defaultLanguage defaultCurrency dateFormat phoneNumberCountryCodes }
        defaultAuthenticationConnection
        jurisdictions { all only }
        reviewTransactions
        faviconLink
        browserTabTitle
        displayMenuBar
        allowLogout
        displayCurrency
        allowPortfolioPerGoal
        clientHostedLoginURL
        clientHostedLoginRedirectUri
        minDepositCents
        minInitialDepositCents
        productShelfCurrencies
        errorPageBackgroundColor
        errorPageDescription { ${TRANSLATED_STRING_FIELDS} }
        errorPageMessage { ${TRANSLATED_STRING_FIELDS} }
        errorPageButtonText { ${TRANSLATED_STRING_FIELDS} }
        errorPageBackgroundImage
        errorPageShowNavbar
        enableFullCashTransfers
        enablePartialCashTransfers
        enableAllInKindTransfers
        cashOnHoldToTradeDays
        cashOnHoldToWithdrawDays
        cancelTransferCutoffDays
        enableLoginSettings
        loginSettingsTitle { ${TRANSLATED_STRING_FIELDS} }
        loginUrl
        hideAutoUpdatePrices
        ${useFeeEnableToggle ? 'feeSetting { enabled }' : ''}
        customAttributeLabels {
          id
          attributeName
          objectType
          state
          options {
            label { ${TRANSLATED_STRING_FIELDS} }
            value
            description { ${TRANSLATED_STRING_FIELDS} }
          }
        }
      }
    }
  }
`;

export const CREATE_ENTITY = gql`
  mutation createEntity($input: CreateUserInput!) {
    createUser(input: $input) {
      user {
        id
        affiliateOnly
        type
        entityName
        firstName
        lastName
        primaryEmail
        households {
          id
          organization {
            id
          }
        }
      }
    }
  }
`;

const FETCH_INTEGRATIONS = gql`
  query fetchIntegrations($input: FetchIntegrationsInput!) {
    fetchIntegrations(input: $input) {
      totalCount
      integrations {
        id
        configuration
        provider
        type
        state
        organization {
          id
        }
      }
    }
  }
`;

const FETCH_ROLE = (permissions?: string[]) => gql`
  query FetchRole($roleId: ObjectID!) {
    fetchRole(roleId: $roleId) {
      role {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        permissions
        accessiblePages
        publicRoleProfile { id translatedName { ${TRANSLATED_STRING_FIELDS} } }
        ${permissions && permissions.includes('read:dashboards') ? `dashboards { id translatedName { ${TRANSLATED_STRING_FIELDS} } }` : ''}
        navigationStyle
        configurableTablePages {
          id
          name { ${TRANSLATED_STRING_FIELDS} }
          section {
            icon
            name { ${TRANSLATED_STRING_FIELDS} }
          }
        }
        makeHouseholdDefaultView
      }
    }
  }
`;

const FETCH_ROLE_PAGE_CONFIGURATIONS = gql`
  query fetchRolePageConfigurations($roleId: ObjectID!) {
    fetchRole(roleId: $roleId) {
      role {
        individualPageConfiguration { ${PAGE_CONFIGURATION()} }
        nonIndividualPageConfiguration { ${PAGE_CONFIGURATION()} }
        goalPageConfiguration { ${PAGE_CONFIGURATION()} }
        accountPageConfiguration { ${PAGE_CONFIGURATION()} }
        accountGroupPageConfiguration { ${PAGE_CONFIGURATION()} }
        householdPageConfiguration { ${PAGE_CONFIGURATION()} }
        subAccountPageConfiguration { ${PAGE_CONFIGURATION()} }
        homePageConfiguration { ${HOME_PAGE_CONFIGURATION} }
        individualTableStaticPageWidgetConfiguration { ${STATIC_PAGE_WIDGET_CONFIGURATION} }
        nonIndividualTableStaticPageWidgetConfiguration { ${STATIC_PAGE_WIDGET_CONFIGURATION} }
        householdTableStaticPageWidgetConfiguration { ${STATIC_PAGE_WIDGET_CONFIGURATION} }
        accountGroupTableStaticPageWidgetConfiguration { ${STATIC_PAGE_WIDGET_CONFIGURATION} }
      }
    }
  }
`;

const ACTIVE_ORG_SKELETON = {
  applicableLocalization: LOCALIZATION_DEFAULTS,
  jurisdictions: { all: true },
};

export const UserContext = createContext<UserContextType>({
  userContext: {},
  setUserContext: () => {},
  userId: undefined,
  setUserId: () => {},
  activeOrganization: ACTIVE_ORG_SKELETON,
  setActiveOrganizationId: () => {},
  activeEntity: {},
  setActiveEntity: () => {},
  activeHousehold: undefined,
  setActiveHousehold: () => {},
  organizationIds: [],
  activeLanguage: undefined,
  setActiveLanguage: () => {},
  setOrganizationIds: () => {},
  activeCurrency: '',
  setActiveCurrency: () => {},
  availableCurrencies: [],
  setAvailableCurrencies: () => {},
  loading: true,
  organizationLoading: true,
  organizationFetchFailed: false,
  integrations: [],
  custodianConnection: { accountTypeSettings: [] },
  baseCurrency: '',
  displayMenuBar: false,
  closed: false,
  setClosed: () => {},
  refetch: () => {},
  upcomingDowntimeSchedules: [],
  applicableDowntimeSchedule: undefined,
  featureFlags: new Set(),
  organizationFeatureFlags: new Set(),
  accessiblePages: new Set(),
  permissions: new Set(),
});

export const UserContextConsumer = UserContext.Consumer;

const UserContextProvider = ({ children }: { children: any }) => {
  const setTheme = useSetTheme();
  const apolloClient = useApolloClient();
  const {
    isEmbedded,
    permissions,
    entityRoleIdRef,
    languageRef,
    appLogout,
    setSettings,
    settings,
  } = useAuthContext();

  const [userContext, setUserContext] = useState<UserContextProps>({});
  const [userId, setUserId] = useState<string | undefined>();
  const { value: closed = false, set: setClosed } =
    useLocalStorageValue<boolean>('closed', {
      defaultValue: false,
      initializeWithValue: true,
    });
  const { set: setActiveOrganizationData } =
    useSessionStorageValue<ActiveOrganizationProps>('activeOrganizationData', {
      initializeWithValue: false,
    });
  const [loading, setLoading] = useState(true);

  const [activeHousehold, setActiveHousehold] = useState<
    ClientGroup | undefined
  >(undefined);
  const [activeOrganizationId, setActiveOrganizationId] =
    useActiveOrganizationId();
  const [activeOrganization, setActiveOrganization] =
    useState<ActiveOrganizationProps>(ACTIVE_ORG_SKELETON);
  const displayMenuBar = useMemo(
    () => (isEmbedded ? !!activeOrganization?.displayMenuBar : true),
    [isEmbedded, activeOrganization?.displayMenuBar],
  );
  const custodianConnection = useMemo<CustodianConnection>(
    () => activeOrganization?.defaultCustodianConnection ?? {},
    [activeOrganization?.defaultCustodianConnection],
  );
  const [activeEntity, setActiveEntity] = useState<ActiveEntityProps>();
  const [activeLanguage, setActiveLanguage] = useState<Languages>(
    getOrgDefaultLanguage({ asEnum: true }),
  );
  const [activeCurrency, setActiveCurrency] = useState<string>(
    LOCALIZATION_DEFAULTS.defaultCurrency ?? 'CAD',
  );
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const entitiesFetchStartedRef = useRef(false);
  const entitiesFetchCompletedRef = useRef(false);

  const { data, refetch: refetchSlim } = useQuery(ME_SLIM(permissions), {
    onError: () => appLogout,
  });

  const [fetchMeEntities, { refetch: refetchEntitiesQuery }] = useLazyQuery(
    ME_ENTITIES(permissions),
    { onError: () => appLogout },
  );

  const { data: integrationsData } = useQuery(FETCH_INTEGRATIONS, {
    skip: !activeOrganizationId || !permissions?.includes('read:integrations'),
    variables: {
      input: { filter: { organizationId: activeOrganizationId } },
    },
  });
  const integrations: Integration[] =
    integrationsData?.fetchIntegrations?.integrations ?? [];

  const [fetchRole] = useLazyQuery(FETCH_ROLE(permissions));
  const [fetchRolePageConfigurations] = useLazyQuery(
    FETCH_ROLE_PAGE_CONFIGURATIONS,
  );

  // This is used to fetch the page configurations for the user's role
  // It is important to use the me query as the clients sometimes don't have the role permissions
  // and therefore cannot use the fetchRolePageConfigurations query
  const { data: meRolePageConfigurationsData } = useQuery(
    ME_ROLE_PAGE_CONFIGURATIONS,
  );

  const accessiblePages = useMemo(() => {
    return new Set(userContext.role?.accessiblePages ?? []);
  }, [userContext.role?.accessiblePages]);

  const permissionsSet = useMemo(() => {
    return new Set(userContext.role?.permissions ?? []);
  }, [userContext.role?.permissions]);

  const [createEntity] = useMutation(CREATE_ENTITY);

  const featureFlags = useFlagsmithFlags(activeOrganization);

  const {
    data: organizationData,
    previousData: organizationPreviousData,
    loading: organizationDataLoading,
    error: organizationDataError,
  } = useQuery(
    FETCH_ORGANIZATION(
      permissions?.includes('read:custodian_connection') ?? false,
      featureFlags.has(FlagsmithFlags.use_fee_enable_toggle),
    ),
    {
      variables: { organizationId: activeOrganizationId ?? '' },
      skip: !activeOrganizationId,
      onError: (error) => {
        if (
          error.graphQLErrors.some(
            (e) => e.extensions?.code === 'NOT_ACCESSIBLE',
          )
        ) {
          setActiveOrganizationId(undefined);
        }
      },
    },
  );

  const { applicableDowntimeSchedule, upcomingDowntimeSchedules } =
    useDowntimeSchedules(activeOrganization?.id);

  const organizationFeatureFlags = useMemo(() => {
    return new Set(activeOrganization?.availableFeatureFlags ?? []);
  }, [activeOrganization?.availableFeatureFlags]);

  const isConditionalPageConfigurationEnabled = featureFlags.has(
    FlagsmithFlags.conditional_account_page_configuration,
  );

  const [fetchAccountPageConfiguration] = useLazyQuery(
    FETCH_PAGE_CONFIGURATION(isConditionalPageConfigurationEnabled),
  );

  const baseCurrency =
    activeOrganization?.applicableLocalization?.defaultCurrency ?? 'CAD';

  /* user profile (ME slim) has been loaded – show UI first, preserve entities until phase 2 */
  const hasEntityPermissions =
    permissions?.includes('read:organization_user_entities') ||
    permissions?.includes('read:rep_code');

  useEffect(() => {
    if (data && meRolePageConfigurationsData) {
      // For entity-level users who need entities, defer setLoading(false) until the
      // entity-specific role is resolved in updateUserContextRole (Phase 2b). Without
      // this guard the router renders with the org-level accessiblePages, causing
      // FallbackRoute to briefly navigate to /clients before the correct role applies.
      // as reported in OVT-5010
      // Use role permissions from ME_SLIM as a fallback for when auth permissions
      // haven't been populated yet. In a fresh session (e.g. incognito), ME_SLIM
      // can resolve before setPermissions fires, making `permissions` an empty
      // array and causing needsEntities to evaluate as false — which would fire
      // setLoading(false) immediately with the org-level role still in context.
      // data.me.role.permissions is always returned by ME_SLIM unconditionally. OVT-5010
      const meRolePermissions = data.me?.role?.permissions ?? [];
      const hasEntityPermissionsForGuard =
        permissions?.includes('read:organization_user_entities') ||
        permissions?.includes('read:rep_code') ||
        meRolePermissions.includes('read:organization_user_entities') ||
        meRolePermissions.includes('read:rep_code');
      const isEntityUserNeedingEntities =
        data.me?.accessType === OrganizationUserAccessTypes.ENTITY &&
        !!(
          data?.me &&
          (data.me.role?.navigationStyle !== NavigationStyles.FULL ||
            data.me.accessType === OrganizationUserAccessTypes.REP_CODE ||
            /*
            Adding this condition to prevent the loading spinner from showing for
            entity users who don't have the makeHouseholdDefaultView permission and
            NavigationStyles is SIMPLE - TO Resolve OVT-6614
            */
            data.me.role?.makeHouseholdDefaultView) &&
          hasEntityPermissionsForGuard
        );
      if (!isEntityUserNeedingEntities) {
        setLoading(false);
      }
      setUserContext((prev) => ({
        ...data.me,
        role: {
          ...prev.role,
          ...data.me?.role,
          ...meRolePageConfigurationsData?.me?.role,
        },
        entities: hasEntityPermissions ? prev.entities : [],
        repCodeEntities: hasEntityPermissions ? prev.repCodeEntities : [],
      }));
      setActiveLanguage(
        data.me?.language ?? getOrgDefaultLanguage({ asEnum: true }),
      );
      setSettings({
        ...settings,
        enableInactivityTimeOut: data.me?.organization?.enableInactivityTimeOut,
        inactivityTimeoutInMinutes:
          data.me?.organization?.inactivityTimeoutInMinutes,
        clientHostedLoginRedirectUri:
          data.me?.organization?.clientHostedLoginRedirectUri,
        clientHostedLoginURL: data.me?.organization?.clientHostedLoginURL,
        auth0RedirectUri: data.me?.organization?.logoutRedirectUrl,
        enableSingleLogout: data.me?.organization?.enableSingleLogout,
      });
    }
  }, [data, meRolePageConfigurationsData]);

  /* Phase 2: load entities after slim ME (avoids blocking UI when there are many entities).
   * Skip for FULL navigation style (e.g. CST) as entity list is not required in that context. */
  const needsEntities =
    data?.me &&
    (data.me.role?.navigationStyle !== NavigationStyles.FULL ||
      data.me.accessType === OrganizationUserAccessTypes.REP_CODE ||
      /*
      Adding this condition to prevent the loading spinner from showing for
      entity users who don't have the makeHouseholdDefaultView permission and
      NavigationStyles is SIMPLE - TO Resolve OVT-6614
      */
      data.me.role?.makeHouseholdDefaultView) &&
    (permissions?.includes('read:organization_user_entities') ||
      permissions?.includes('read:rep_code'));

  useEffect(() => {
    if (!needsEntities || entitiesFetchStartedRef.current) return;
    entitiesFetchStartedRef.current = true;
    fetchMeEntities({
      onCompleted: (entitiesData) => {
        entitiesFetchCompletedRef.current = true;
        if (entitiesData?.me) {
          setUserContext((prev) => ({
            ...prev,
            entities: entitiesData.me.entities ?? prev.entities,
            repCodeEntities:
              entitiesData.me.repCodeEntities ?? prev.repCodeEntities,
            entitiesCount: entitiesData.me.entitiesCount ?? prev.entitiesCount,
          }));
        }
        // Safety net: if the entity user has no entities, setActiveEntity
        // never fires so updateUserContextRole never reaches the unblocking path.
        // Unblock here so loading never hangs forever.
        const hasEntities = (entitiesData?.me?.entities?.length ?? 0) > 0;
        if (!hasEntities) {
          setLoading(false);
        }
      },
    });
  }, [needsEntities]);

  const isFirstLanguageRender = useRef(true);

  /* update backend language and re-fetch all active queries when language changes */
  useEffect(() => {
    setBackendLanguage(activeLanguage);
    languageRef.current = getBackendLanguage();
    if (isFirstLanguageRender.current) {
      isFirstLanguageRender.current = false;
      return;
    }
    apolloClient.refetchQueries({ include: 'active' });
  }, [activeLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  /* user profile (ME) has been loaded AND there's no organization set - use their default. Use userContext.entities (phase 2) when available. */
  useEffect(() => {
    if (data?.me?.organization?.id) {
      if (!activeOrganizationId)
        setActiveOrganizationId(data.me.organization.id);
    }
    const entitiesRaw =
      userContext.accessType === OrganizationUserAccessTypes.REP_CODE
        ? userContext.repCodeEntities
        : userContext.entities;
    type EntityEntry = {
      entity: ActiveEntityProps & { affiliateOnly?: boolean };
    };
    const entitiesList = (entitiesRaw ?? []) as EntityEntry[];
    if (entitiesList.length) {
      if (!activeEntity?.id) {
        const urlPath = window.location.pathname;
        const urlEntity = entitiesList.find((e) =>
          urlPath.includes(e.entity.id || ''),
        );
        const firstNonAffiliateEntity = entitiesList.find(
          (e) => !e.entity.affiliateOnly,
        );
        const entity = urlEntity?.entity ?? firstNonAffiliateEntity?.entity;
        setActiveEntity(entity);

        const householdsWithAccess = filterHouseholdsWithAccess(
          entity?.households ?? [],
          entity?.id,
          {
            accessType: userContext.accessType,
            entities: userContext.entities,
          },
        );
        if (householdsWithAccess.length > 0)
          setActiveHousehold(householdsWithAccess[0]);
      }
    }
  }, [
    data,
    userContext.entities,
    userContext.repCodeEntities,
    userContext.accessType,
    activeOrganizationId,
    setActiveOrganizationId,
    activeEntity,
    setActiveEntity,
    setActiveHousehold,
  ]);

  useEffect(() => {
    const checkActiveRole =
      activeEntity?.id &&
      userContext.entities &&
      userContext.accessType !== OrganizationUserAccessTypes.REP_CODE;

    const activeRole = checkActiveRole
      ? userContext.entities?.find((e) => e.entity.id === activeEntity?.id)
          ?.role
      : undefined;

    const roleId = activeRole?.id ?? data?.me?.role?.id;
    if (!roleId) return;

    async function updateUserContextRole() {
      // Use data?.me?.accessType (Apollo query result) NOT userContext.accessType.
      // This effect can fire in the same batch as Phase 1's setUserContext, making
      // userContext a stale closure that still reads undefined for accessType.
      // data is always current because data?.me?.role?.id is a dependency.
      const isEntityUserWaiting =
        data?.me?.accessType === OrganizationUserAccessTypes.ENTITY &&
        !checkActiveRole;

      let roleData: Awaited<ReturnType<typeof fetchRole>>['data'];
      let rolePageConfigurationsData: Awaited<
        ReturnType<typeof fetchRolePageConfigurations>
      >['data'];
      try {
        ({ data: roleData } = await fetchRole({ variables: { roleId } }));
        ({ data: rolePageConfigurationsData } =
          await fetchRolePageConfigurations({ variables: { roleId } }));
        setUserContext((prev) => ({
          ...prev,
          role: {
            ...roleData.fetchRole.role,
            ...rolePageConfigurationsData.fetchRole.role,
          },
        }));
      } finally {
        // Unblock the loading spinner. For entity users, only unblock once the
        // entity-specific role is applied (checkActiveRole = true). If the entity
        // isn't resolved yet, stay on the spinner until the effect re-fires with
        // the correct role. Always unblocks on error so we never hang forever.
        if (!isEntityUserWaiting) {
          setLoading(false);
        }
      }

      // If we reach here, the try block completed without throwing, so both
      // roleData and rolePageConfigurationsData are guaranteed to be defined.
      if (!roleData || !rolePageConfigurationsData) return;

      const accountPageConfigurationId =
        rolePageConfigurationsData.fetchRole.role?.accountPageConfiguration?.id;

      if (!isConditionalPageConfigurationEnabled || !accountPageConfigurationId)
        return;

      const { data: accountPageConfigurationData } =
        await fetchAccountPageConfiguration({
          variables: { pageConfigurationId: accountPageConfigurationId },
        });

      const accountPageConfiguration =
        accountPageConfigurationData?.fetchPageConfiguration?.pageConfiguration;

      setUserContext((prev) => ({
        ...prev,
        role: {
          ...roleData.fetchRole.role,
          ...rolePageConfigurationsData.fetchRole.role,
          accountPageConfiguration,
        },
      }));
    }

    updateUserContextRole();
    entityRoleIdRef.current = activeRole?.id;
  }, [
    activeEntity,
    userContext.entities,
    userContext.role?.id,
    data?.me?.role?.id,
  ]);

  /* Automatically creates Individual entity during sign-up (entities loaded and empty) */
  useEffect(() => {
    const entitiesLength = userContext.entities?.length ?? 0;
    const loadsEntities = permissions?.includes(
      'read:organization_user_entities',
    );
    const skipEntitiesFetch =
      data?.me?.role?.navigationStyle === NavigationStyles.FULL;
    const entitiesReady =
      !loadsEntities || entitiesFetchCompletedRef.current || skipEntitiesFetch;
    if (
      entitiesReady &&
      entitiesLength === 0 &&
      userContext.accessType === OrganizationUserAccessTypes.ENTITY &&
      userContext.organization?.defaultSignUpRole?.id ===
        userContext.role?.id &&
      permissions?.includes('write:client_basic')
    ) {
      setLoading(true);
      createEntity({
        variables: {
          input: {
            type: EntityTypes.INDIVIDUAL,
            language: getOrgDefaultLanguage({ asEnum: true }),
            primaryEmail: userContext.email,
            phone: userContext.phone ?? undefined,
            firstName: userContext.firstName ?? undefined,
            lastName: userContext.lastName ?? undefined,
            organizationId: userContext.organization?.id,
          },
        },
        onCompleted: async (resp) => {
          if (resp.createUser.user.id) {
            // TODO: add GraphQL subscription to confirm it's ready to redirect user
            await delay(2000); // Add 2s delay so that workflow can be created for the new entity
            setActiveEntity(resp.createUser.user);
            setLoading(false);
          }
        },
      });
    }
  }, [
    data?.me?.role?.navigationStyle,
    userContext.entities,
    userContext.accessType,
    userContext.organization?.defaultSignUpRole?.id,
    userContext.organization?.id,
    userContext.role?.id,
    permissions,
  ]);

  /* organization profile (FETCH_ORGANIZATION) has been loaded */
  useEffect(() => {
    const rawOrganization = organizationData?.fetchOrganization?.organization;
    if (!rawOrganization) return;

    const org: any = omitDeep(rawOrganization, '__typename');
    const baseColor = org.theme?.sideBarColor || '#22394F';
    org.theme = {
      ...org.theme,
      graphColors: [
        baseColor,
        adjust(baseColor, -10),
        adjust(baseColor, -20),
        adjust(baseColor, -30),
        adjust(baseColor, -40),
        adjust(baseColor, 10),
        adjust(baseColor, 20),
        adjust(baseColor, 30),
        adjust(baseColor, 40),
        adjust(baseColor, 50),
      ],
    };
    if (!org.applicableLocalization)
      org.applicableLocalization = LOCALIZATION_DEFAULTS;

    setActiveOrganization(org as ActiveOrganizationProps);

    if (
      i18n.options.fallbackLng !== org.applicableLocalization.defaultLanguage
    ) {
      i18n.options.fallbackLng = org.applicableLocalization.defaultLanguage;
    }

    setActiveOrganizationData(org);
  }, [organizationData, setActiveOrganizationData]);

  useEffect(() => {
    const themeTokens = activeOrganization.themeTokens;
    setTheme({
      palette: themeTokens?.ref?.palette,
      color: themeTokens?.sys?.color,
      font: themeTokens?.sys?.font,
      borderRadius: themeTokens?.sys?.borderRadius,
      elevation: themeTokens?.sys?.elevation,
      spacing: themeTokens?.sys?.spacing,
      component: themeTokens?.comp,
    });
  }, [activeOrganization.themeTokens, setTheme]);

  useEffect(() => {
    const orgDefaultLanguageCode =
      activeOrganization?.applicableLocalization?.defaultLanguage;
    if (data?.me?.language || !orgDefaultLanguageCode) return;

    const orgDefaultLanguage =
      languageCodeToLanguageMap[orgDefaultLanguageCode as LanguageCodes];
    if (orgDefaultLanguage) setActiveLanguage(orgDefaultLanguage);
  }, [
    activeOrganization?.applicableLocalization?.defaultLanguage,
    data?.me?.language,
  ]);

  useEffect(() => {
    setActiveCurrency(baseCurrency);
  }, [activeOrganization?.id, baseCurrency]);

  const refetch = useCallback(() => {
    refetchSlim();
    if (entitiesFetchStartedRef.current && refetchEntitiesQuery) {
      refetchEntitiesQuery().then((result) => {
        if (result?.data?.me) {
          setUserContext((prev) => ({
            ...prev,
            entities: result.data.me.entities ?? prev.entities,
            repCodeEntities:
              result.data.me.repCodeEntities ?? prev.repCodeEntities,
            entitiesCount: result.data.me.entitiesCount ?? prev.entitiesCount,
          }));
        }
      });
    }
  }, [refetchSlim, refetchEntitiesQuery]);

  // FETCH_ORGANIZATION is rebuilt from `featureFlags`, which update after
  // `flagsmith.identify` runs once the org loads. That changes the GraphQL
  // document, so Apollo treats it as a new query and briefly resets
  // `organizationData` to undefined while it refetches. Fall back to
  // `previousData` (the last completed result) so a document swap for the
  // already-loaded org does not momentarily read as "no org loaded" and tear
  // the app down to a BootSpinner. On a real org switch `activeOrganizationId`
  // changes and will not match the stale previous result, so booting still
  // occurs correctly.
  const resolvedOrganizationData = organizationData ?? organizationPreviousData;

  // The app can only render safely once the loaded organization profile matches
  // the organization the user is actually viewing. Until then the context still
  // holds the empty skeleton (first load) or the previously active org (after a
  // switch), so proceeding would render stale/invalid org context.
  const hasMatchingActiveOrganization =
    !!activeOrganizationId &&
    activeOrganizationId ===
      resolvedOrganizationData?.fetchOrganization?.organization?.id &&
    activeOrganization?.id === activeOrganizationId;

  // A terminal organization fetch failure: the query settled with a
  // non-NOT_ACCESSIBLE error (NOT_ACCESSIBLE is recovered from by falling back
  // to the user's default org) and we have no usable org to render. This must be
  // surfaced as an error state rather than silently rendering the app. A failure
  // while we still have a matching org (e.g. a background refetch) is ignored so
  // it does not tear down a working session.
  const organizationFetchFailed =
    !organizationDataLoading &&
    !!organizationDataError &&
    !organizationDataError.graphQLErrors.some(
      (e) => e.extensions?.code === 'NOT_ACCESSIBLE',
    ) &&
    !hasMatchingActiveOrganization;

  // Keep booting while we have no matching org yet, unless the fetch has
  // terminally failed (in which case the layout renders the error page).
  const organizationLoading =
    !hasMatchingActiveOrganization && !organizationFetchFailed;

  return (
    <UserContext.Provider
      value={{
        userContext,
        setUserContext,
        userId,
        setUserId,
        activeOrganization,
        featureFlags,
        organizationFeatureFlags,
        accessiblePages,
        permissions: permissionsSet,
        setActiveOrganizationId,
        activeEntity,
        setActiveEntity,
        activeHousehold,
        setActiveHousehold,
        activeLanguage,
        setActiveLanguage,
        organizationIds,
        setOrganizationIds,
        activeCurrency,
        setActiveCurrency,
        availableCurrencies,
        setAvailableCurrencies,
        closed,
        setClosed,
        loading,
        organizationLoading,
        organizationFetchFailed,
        integrations,
        custodianConnection,
        baseCurrency,
        displayMenuBar,
        refetch,
        upcomingDowntimeSchedules,
        applicableDowntimeSchedule,
      }}
    >
      {activeOrganization?.id && (
        <DocumentHead
          faviconLink={activeOrganization.faviconLink ?? null}
          browserTabTitle={activeOrganization.browserTabTitle}
        />
      )}
      {children}
    </UserContext.Provider>
  );
};

export const usePermission = (permission: string): boolean => {
  const { permissions } = useContext(UserContext);

  return useMemo(() => {
    return permissions.has(permission);
  }, [permissions, permission]);
};

export const usePagePermission = (page: Pages): boolean => {
  const { accessiblePages } = useContext(UserContext);

  return useMemo(() => {
    return accessiblePages.has(page);
  }, [accessiblePages, page]);
};

export const useFeatureFlag = (flag: FlagsmithFlags) => {
  const { featureFlags } = useContext(UserContext);

  return useMemo(() => {
    return featureFlags.has(flag);
  }, [featureFlags, flag]);
};

export const useOrganizationFeatureFlag = (flag: FeatureFlagTypes) => {
  const { organizationFeatureFlags } = useContext(UserContext);

  return useMemo(() => {
    return organizationFeatureFlags.has(flag);
  }, [organizationFeatureFlags, flag]);
};

export const useLimitedAccess = () => {
  const { userContext, activeEntity } = useContext(UserContext);

  const limitedAccess =
    userContext.accessType === OrganizationUserAccessTypes.ENTITY &&
    userContext.entities?.some(
      (en) => en.entity?.id === activeEntity?.id && en.accounts.length > 0,
    );

  return { isLimitedAccess: !!limitedAccess };
};

export default UserContextProvider;
